import { memo, useEffect, useMemo, useState } from 'react';
import { formatFileSize } from '../utils/format-file-size';
import {
  ArrowDropdownSmallWithoutBg,
  CloseSmall,
  PaperClip,
  Trash,
} from '@shared/assets/icons/components';
import { Button, Flex, Input, Progress } from 'antd';
import styles from './attachment-item.module.scss';
import { Text } from '@shared/ui/antd';
import classNames from 'classnames';
import type { CreateFileFormValuesApi } from '../lib/types';
import { useTranslation } from 'react-i18next';

interface AttachmentItemProps {
  fileKey: string;
  fileName: string;
  fileSize: number;
  isDefault?: boolean;
  abortController?: AbortController | null;
  uploadState?: { percentage: number; error: boolean };
  onRemove?: (fileKey: string, isUploaded: boolean, isDefault: boolean) => void;
  onFormChange?: (
    fileKey: string,
    values: Partial<CreateFileFormValuesApi>,
    isValid: boolean,
  ) => void;
  variant?: 'default' | 'compact';
}

export const AttachmentItem = memo<AttachmentItemProps>((props) => {
  const { t } = useTranslation('translation', { keyPrefix: 'knowledgeBase.uploadForm' });
  const { t: tCommon } = useTranslation('translation');
  const {
    fileKey,
    fileName,
    fileSize,
    isDefault = false,
    abortController,
    uploadState,
    onRemove,
    onFormChange,
    variant = 'default',
  } = props;

  const [isFormExpanded, setIsFormExpanded] = useState(true);

  const defaultTitle = useMemo(() => {
    const parts = fileName.split('.');
    if (parts.length <= 1) return fileName;
    parts.pop();
    return parts.join('.');
  }, [fileName]);

  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState('');

  const { percentage = 0, error = false } = uploadState ?? {};
  const uploadCompleted = percentage === 100;
  const uploadFailed = error;
  const isUploading = uploadState != null && !uploadCompleted && !uploadFailed;
  const uploadSucceeded = !uploadFailed && uploadCompleted;

  const handleRemove = () => {
    if (isUploading) {
      abortController?.abort();
    }
    onRemove?.(fileKey, uploadSucceeded, isDefault);
  };

  const toggleFormExpanded = () => setIsFormExpanded((prev) => !prev);

  useEffect(() => {
    if (uploadCompleted || isUploading) {
      setIsFormExpanded(false);
    }
  }, [uploadCompleted, isUploading]);

  useEffect(() => {
    const isValid = title.trim().length > 0;
    onFormChange?.(
      fileKey,
      {
        fileKey,
        title: title.trim(),
        description: description.trim(),
      },
      isValid,
    );
  }, [fileKey, title, description, onFormChange]);

  if (variant === 'compact') {
    return (
      <Flex className={classNames(styles.root, styles.compactRoot)} align="center">
        <Flex gap="8px" align="center" className={styles.compactRow}>
          <PaperClip className={styles.compactIcon} />
          <Text variant="sm" className={styles.compactName}>
            {fileName}
          </Text>
        </Flex>

        <Button
          className={styles.compactRemove}
          icon={<CloseSmall />}
          shape="circle"
          type="text"
          onClick={handleRemove}
          aria-label={tCommon('common.actions.delete')}
        />
      </Flex>
    );
  }

  return (
    <Flex className={styles.root}>
      <Flex justify="space-between" align="center" className={styles.container} vertical>
        <Flex justify="space-between" className={styles.wrapper}>
          <Flex gap="8px" className={styles.wrapper}>
            <PaperClip className={styles.fileIcon} />
            <Flex flex="1" vertical>
              <Flex gap="4px" align="center">
                <Text variant="sm" className={styles.title}>
                  {fileName}
                </Text>

                <Button
                  className={styles.icon}
                  icon={<Trash className={styles.icon} />}
                  shape="circle"
                  type="link"
                  onClick={handleRemove}
                  aria-label={tCommon('common.actions.delete')}
                />
              </Flex>
              <Text variant="xs" className={styles.size}>
                {formatFileSize(fileSize)}
              </Text>
            </Flex>
          </Flex>
          <Button
            className={classNames(styles.toggleButton, styles.icon, {
              [styles.open]: isFormExpanded,
            })}
            icon={<ArrowDropdownSmallWithoutBg color="#234EEE" />}
            shape="circle"
            type="link"
            disabled={uploadCompleted}
            onClick={toggleFormExpanded}
          />
        </Flex>

        <Flex
          vertical
          gap="8px"
          className={classNames(styles.form, {
            [styles.open]: isFormExpanded,
          })}
        >
          <Flex vertical gap="4px">
            <Text variant="xs" className={styles.label}>
              {t('titleLabel')}
            </Text>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('titlePlaceholder')}
              className={styles.input}
              disabled={isUploading || uploadCompleted}
            />
          </Flex>

          <Flex vertical gap="4px">
            <Text variant="xs" className={styles.label}>
              {t('descriptionLabel')}
            </Text>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('descriptionPlaceholder')}
              className={styles.textarea}
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled={isUploading || uploadCompleted}
            />
          </Flex>
        </Flex>

        {percentage > 0 && !uploadSucceeded && (
          <Progress
            status={uploadFailed ? 'exception' : 'active'}
            percent={percentage}
            strokeColor="#ED131C"
          />
        )}
      </Flex>
    </Flex>
  );
});
